/**
 * Chat room list grouping — tenant-configurable sections (Splošno first).
 */

export const DEFAULT_CHAT_LIST_GROUPS = [
  { id: 'general', label: 'Splošno', order: 0 },
  { id: 'conference', label: 'Konference', order: 1 },
  { id: 'rooms', label: 'Klepetalnice', order: 2 },
  { id: 'units', label: 'Organizacijske enote', order: 3 },
];

export function isDefaultChatListGroupId(groupId) {
  return DEFAULT_CHAT_LIST_GROUPS.some((group) => group.id === groupId);
}

export function getRoomDisplayName(room) {
  return room.roomName || room.unitName || room.id;
}

export function isGeneralChatRoom(room) {
  const messageUnit = room.messageUnit || room.id;
  const name = getRoomDisplayName(room).toLowerCase();

  return (
    messageUnit === 'general'
    || room.id === 'general'
    || room.id.endsWith('__general')
    || name === 'splošno'
  );
}

export function isConferenceChatRoom(room) {
  const messageUnit = room.messageUnit || room.id;

  return (
    messageUnit === 'conference'
    || room.id === 'conference'
    || room.id.endsWith('__conference')
  );
}

export function resolveRoomListGroupId(room) {
  if (room.listGroupId) return room.listGroupId;
  if (isGeneralChatRoom(room)) return 'general';
  if (isConferenceChatRoom(room)) return 'conference';
  if (room.isChatRoom || room.roomName) return 'rooms';
  return 'units';
}

export function resolveChatListGroups(tenantConfig) {
  const custom = tenantConfig?.chatListGroups;
  const byId = new Map(
    DEFAULT_CHAT_LIST_GROUPS.map((group) => [group.id, { ...group }]),
  );

  if (Array.isArray(custom)) {
    custom.forEach((group) => {
      if (!group?.id) return;

      const label = group.label || group.id;
      const duplicateLabel = findDuplicateChatListGroupLabel(
        Array.from(byId.values()),
        label,
        group.id,
      );
      if (duplicateLabel) {
        return;
      }

      byId.set(group.id, {
        id: group.id,
        label,
        order: typeof group.order === 'number' ? group.order : 999,
      });
    });
  }

  return Array.from(byId.values()).sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.label.localeCompare(b.label, 'sl');
  });
}

function sortRoomsInGroup(rooms, unreadCounts) {
  return [...rooms].sort((a, b) => {
    const aUnread = unreadCounts[a.id] || 0;
    const bUnread = unreadCounts[b.id] || 0;

    if (aUnread > 0 && bUnread === 0) return -1;
    if (bUnread > 0 && aUnread === 0) return 1;
    if (aUnread !== bUnread) return bUnread - aUnread;

    return getRoomDisplayName(a).localeCompare(getRoomDisplayName(b), 'sl');
  });
}

export function groupChatRooms(rooms, tenantConfig, unreadCounts = {}) {
  const groups = resolveChatListGroups(tenantConfig);
  const groupMap = new Map(groups.map((group) => [group.id, []]));
  const fallbackGroupId = groupMap.has('rooms') ? 'rooms' : groups[0]?.id;

  rooms.forEach((room) => {
    const groupId = resolveRoomListGroupId(room);
    const targetId = groupMap.has(groupId) ? groupId : fallbackGroupId;
    if (targetId) {
      groupMap.get(targetId).push(room);
    }
  });

  return groups
    .filter((group) => (groupMap.get(group.id)?.length || 0) > 0)
    .map((group) => ({
      group,
      rooms: sortRoomsInGroup(groupMap.get(group.id), unreadCounts),
    }));
}

export function slugifyGroupId(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function normalizeChatListGroupLabel(label) {
  return String(label || '')
    .trim()
    .toLocaleLowerCase('sl');
}

export function findDuplicateChatListGroupLabel(groups, label, excludeGroupId = null) {
  const normalized = normalizeChatListGroupLabel(label);
  if (!normalized) {
    return null;
  }

  return groups.find(
    (group) => group.id !== excludeGroupId
      && normalizeChatListGroupLabel(group.label) === normalized,
  ) || null;
}

export function validateChatListGroupLabels(groups) {
  const seen = new Map();

  for (const group of groups) {
    const normalized = normalizeChatListGroupLabel(group.label);
    if (!normalized) {
      continue;
    }

    if (seen.has(normalized)) {
      return `Skupina «${group.label}» je podvojena. Nazivi skupin morajo biti enolični.`;
    }

    seen.set(normalized, group.id);
  }

  return null;
}

const ROLE_ACCESS_LABELS = {
  admin: 'administratorji',
  'local-admin': 'administratorji enot',
};

function formatAllowedUserNames(allowedUsers, tenantUsers) {
  return allowedUsers.map((uid) => {
    const member = tenantUsers.find((item) => item.id === uid);
    return member?.displayName || member?.email || uid;
  });
}

export function describeChatRoomAccess(room, tenantUsers = []) {
  if (room.system) {
    if (room.accessibleToAll) {
      return {
        summary: 'Vsi člani',
        title: 'Dostop imajo vsi aktivni člani sindikata.',
      };
    }

    if (room.accessibleToRoles?.length) {
      const labels = room.accessibleToRoles.map(
        (role) => ROLE_ACCESS_LABELS[role] || role,
      );
      return {
        summary: labels.join(', '),
        title: `Dostop imajo: ${labels.join(', ')}.`,
      };
    }

    return {
      summary: 'Sistemska',
      title: 'Sistemska klepetalnica z fiksno nastavitvijo dostopa.',
    };
  }

  if (!room.allowedUsers?.length) {
    return {
      summary: 'Samo administratorji',
      title: 'Brez izbranih članov imajo dostop le administrator in administratorji enot.',
    };
  }

  const names = formatAllowedUserNames(room.allowedUsers, tenantUsers);
  const summary = names.length <= 2
    ? names.join(', ')
    : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;

  return {
    summary,
    title: names.join(', '),
  };
}

export function resolveChatRoomCreatorLabel(room, tenantUsers, currentUid) {
  if (room.system) {
    return 'Sistemska klepetalnica';
  }

  if (!room.createdBy) {
    return '—';
  }

  if (room.createdBy === currentUid) {
    return 'Vi';
  }

  const user = tenantUsers.find((item) => item.id === room.createdBy);
  return user?.displayName || user?.email || room.createdBy;
}
